/**
 * Members API Client
 * Frontend API calls for managing members (admin operations)
 */

import { apiCall } from '../api';
import type {
  Member,
  MemberFilters,
  MembersResponse,
  PendingMembersResponse,
  UpdateMemberRequest,
  RejectMemberRequest,
  ResetMemberPasswordRequest,
} from '@/types/api';

function buildMemberQueryParams(filters: MemberFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.role) params.append('role', filters.role);
  if (filters.pickupLocationId !== undefined) {
    params.append('pickupLocationId', filters.pickupLocationId.toString());
  }
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  return params;
}

/**
 * Fetch pending members awaiting approval
 * @returns Pending members list with count
 */
export async function fetchPendingMembers(): Promise<PendingMembersResponse> {
  return await apiCall<PendingMembersResponse>('/api/admin/members/pending', {
    method: 'GET',
  });
}

/**
 * Fetch members with optional filtering and pagination
 * @param filters - Search, status, role, location, and pagination options
 * @returns Paginated members list
 */
export async function fetchMembers(filters: MemberFilters = {}): Promise<MembersResponse> {
  const params = buildMemberQueryParams(filters);

  const url = params.toString()
    ? `/api/admin/members?${params.toString()}`
    : '/api/admin/members';

  return await apiCall<MembersResponse>(url, {
    method: 'GET',
  });
}

/**
 * Export every member matching the current table filters as an XLSX download.
 */
export async function exportMembersXLSX(filters: MemberFilters = {}): Promise<void> {
  const params = buildMemberQueryParams({
    search: filters.search,
    status: filters.status,
    role: filters.role,
    pickupLocationId: filters.pickupLocationId,
  });
  const url = params.toString()
    ? `/api/admin/members/export?${params.toString()}`
    : '/api/admin/members/export';
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(error.error || `Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]
    || `members-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Fetch a single member by ID
 * @param id - Member ITS ID (as string)
 * @returns Member details
 */
export async function fetchMemberById(id: string): Promise<Member> {
  return await apiCall<Member>(`/api/admin/members/${id}`, {
    method: 'GET',
  });
}

/**
 * Update member details (approval + editing)
 * @param id - Member ITS ID (as string)
 * @param data - Member data to update
 * @returns Updated member
 */
export async function updateMember(id: string, data: UpdateMemberRequest): Promise<Member> {
  return await apiCall<Member>(`/api/admin/members/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Reject a pending member
 * @param id - Member ITS ID (as string)
 * @param reason - Optional rejection reason
 */
export async function rejectMember(id: string, reason?: string): Promise<void> {
  const payload: RejectMemberRequest = reason ? { reason } : {};
  await apiCall<void>(`/api/admin/members/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Admin-assisted password reset for a member who lost access to their account
 * @param id - Member ITS ID (as string)
 * @param newPassword - The new password to set for the member
 */
export async function resetMemberPassword(id: string, newPassword: string): Promise<void> {
  const payload: ResetMemberPasswordRequest = { newPassword };
  await apiCall<void>(`/api/admin/members/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Permanently delete an inactive member and their RSVP/feedback history
 * @param id - Member ITS ID (as string)
 */
export async function deleteMember(id: string): Promise<void> {
  await apiCall<void>(`/api/admin/members/${id}`, {
    method: 'DELETE',
  });
}
